import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaBolt, FaPalette, FaMobileAlt, FaMapMarkerAlt, FaCheck, FaRocket, FaLightbulb, FaUsers } from 'react-icons/fa';
import AnimatedSection from '../common/AnimatedSection';

const AdvantagesSection = () => {
  const advantages = [
    {
      icon: <FaBolt className="text-4xl text-blue-500 group-hover:text-blue-600 transition-colors duration-300" />,
      title: 'Rapidité',
      description: 'Des sites web ultra-rapides pour une expérience utilisateur optimale et un meilleur référencement.',
      delay: 0.1
    },
    {
      icon: <FaPalette className="text-4xl text-indigo-500 group-hover:text-indigo-600 transition-colors duration-300" />,
      title: 'Design moderne',
      description: 'Des interfaces élégantes et intuitives qui reflètent l\'identité de votre entreprise.',
      delay: 0.2
    },
    {
      icon: <FaMobileAlt className="text-4xl text-blue-500 group-hover:text-blue-600 transition-colors duration-300" />,
      title: '100% Responsive',
      description: 'Une expérience parfaite sur tous les appareils : ordinateurs, tablettes et smartphones.',
      delay: 0.3
    },
    {
      icon: <FaMapMarkerAlt className="text-4xl text-indigo-500 group-hover:text-indigo-600 transition-colors duration-300" />,
      title: 'Expertise locale',
      description: 'Un développeur basé en PACA pour un accompagnement personnalisé et de proximité.',
      delay: 0.4
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <AnimatedSection className="text-center mb-16" direction="up">
          <motion.div 
            className="inline-block mb-4 bg-blue-100 text-blue-600 px-4 py-1 rounded-full font-medium text-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            NOTRE EXPERTISE
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">Pourquoi choisir Nexlyx ?</h2>
          <motion.div 
            className="h-1 w-24 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto mb-6"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Des avantages concrets pour faire de votre projet web un succès et vous démarquer de la concurrence.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {advantages.map((advantage, index) => (
            <AnimatedSection 
              key={index} 
              className="bg-white p-8 rounded-xl shadow-xl text-center h-full flex flex-col group hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
              direction="up"
              delay={advantage.delay}
            >
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                  {advantage.icon}
                </div>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-800 group-hover:text-blue-700 transition-colors duration-300">{advantage.title}</h3>
              <p className="text-gray-600 group-hover:text-gray-700 flex-grow transition-colors duration-300">{advantage.description}</p>
            </AnimatedSection>
          ))}
        </div>

        {/* Pourquoi nous choisir */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <AnimatedSection direction="left" delay={0.1}>
              <h3 className="text-3xl font-bold mb-6 text-gray-800">Votre succès est notre priorité</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Chez Nexlyx, nous ne nous contentons pas de créer des sites web. Nous concevons des solutions digitales complètes qui vous aident à atteindre vos objectifs commerciaux et à vous démarquer dans un monde numérique en constante évolution.  
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: <FaCheck className="text-green-500" />, text: "Approche personnalisée pour chaque projet" },
                  { icon: <FaRocket className="text-blue-500" />, text: "Solutions évolutives qui grandissent avec votre entreprise" },
                  { icon: <FaLightbulb className="text-yellow-500" />, text: "Conseils stratégiques pour maximiser votre présence en ligne" },
                  { icon: <FaUsers className="text-indigo-500" />, text: "Support réactif et accompagnement continu" }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-center"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + (index * 0.1) }}
                  >
                    <div className="mr-3 w-6 h-6 flex-shrink-0 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <p className="text-gray-700">{item.text}</p>
                  </motion.div>
                ))}
              </div>
              
              <motion.div 
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Link to="/contact" className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium px-6 py-3 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                  Discutons de votre projet
                </Link>
              </motion.div>
            </AnimatedSection>
            
            <AnimatedSection direction="right" delay={0.3}>
              {/* Stats Section */}
              <div className="grid grid-cols-1 gap-6">
                <motion.div 
                  className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg relative overflow-hidden group"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 z-0"></div>
                  <div className="relative z-10">
                    <h3 className="text-5xl font-bold mb-2">+50%</h3>
                    <p className="text-lg text-blue-100">Amélioration du taux de conversion</p>
                    <p className="text-sm text-blue-200 mt-2">Nos sites web sont conçus pour convertir les visiteurs en clients</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-6 rounded-xl shadow-lg relative overflow-hidden group"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 z-0"></div>
                  <div className="relative z-10">
                    <h3 className="text-5xl font-bold mb-2">+90%</h3>
                    <p className="text-lg text-indigo-100">Performance PageSpeed Insights</p>
                    <p className="text-sm text-indigo-200 mt-2">Des sites ultra-rapides pour une expérience utilisateur optimale</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-6 rounded-xl shadow-lg relative overflow-hidden group"
                  whileHover={{ scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 z-0"></div>
                  <div className="relative z-10">
                    <h3 className="text-5xl font-bold mb-2">100%</h3>
                    <p className="text-lg text-blue-100">Satisfaction client</p>
                    <p className="text-sm text-blue-200 mt-2">Notre engagement: votre satisfaction totale</p>
                  </div>
                </motion.div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;